import axios from "../../../../../api/axios";
import { toast } from 'react-toastify';

export const ENDPOINTS = {
  BASE: "/v2/inspections/tracker",
  CREATE: "/v2/inspections/tracker",
  GET_ALL: "/v2/inspections/tracker",
  GET_BY_ID: "/v2/inspections/tracker/:id",
  UPDATE: "/v2/inspections/tracker/:id",
  DELETE: "/v2/inspections/tracker/:id",
  SIGNED_URLS: "/v2/inspections/tracker/signed-urls",
  UPLOAD_REQUEST: "/v2/inspections/tracker/:id/attachments/request-upload",
  CONFIRM_UPLOAD: "/v2/inspections/tracker/:id/attachments/confirm",
  REMOVE_ATTACHMENT: "/v2/inspections/tracker/:id/items/:index/attachments",
  ITEM_COMPLETION: "/v2/inspections/tracker/:id/items/:index/completion",
  SKIP_ITEM: "/v2/inspections/tracker/:id/items/:index/skip",
  SCHEDULE: "/v2/inspections/tracker/schedule",
  YEARS: "/v2/inspections/tracker/years",
  OPEN_CYCLE: "/v2/inspections/tracker/:id/items/:index/open-cycle",
  REMOVE_ITEM: "/v2/inspections/tracker/:id/items/:index",
  HISTORY: "/v2/inspections/tracker/:id/history",
  EXPORT_EXCEL: "/v2/inspections/tracker/export-excel",
  TYPES_BASE: "/v2/inspections/types",
  TYPE_BY_ID: "/v2/inspections/types/:id",
  FREQUENCIES_BASE: "/v2/inspections/frequencies",
  FREQUENCY_BY_ID: "/v2/inspections/frequencies/:id",
};

const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage || "An unexpected error occurred";
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

// The server asks before adding a second schedule of a type the building already
// has. That is a question for the user, not an error: pass it up untoasted.
const duplicateTypeError = (error) => {
  if (error.response?.status === 409 && error.response?.data?.code === 'DUPLICATE_TYPE') {
    const e = new Error(error.response.data.message);
    e.duplicateType = true;
    return e;
  }
  return null;
};

export const createInspection = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE, payload, { headers: { "Content-Type": "application/json" } });
    return response.data.data;
  } catch (error) {
    const duplicate = duplicateTypeError(error);
    if (duplicate) throw duplicate;
    handleApiError(error, "Failed to create inspection record");
  }
};

export const getInspections = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_ALL, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inspection records");
  }
};

export const getInspectionById = async (id) => {
  try {
    const url = ENDPOINTS.GET_BY_ID.replace(":id", id);
    const response = await axios.get(url);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inspection details");
  }
};

export const updateInspection = async (id, payload) => {
  try {
    const url = ENDPOINTS.UPDATE.replace(":id", id);
    const response = await axios.patch(url, payload, { headers: { "Content-Type": "application/json" } });
    return response.data.data;
  } catch (error) {
    const duplicate = duplicateTypeError(error);
    if (duplicate) throw duplicate;
    handleApiError(error, "Failed to update inspection record");
  }
};

export const deleteInspection = async (id) => {
  try {
    const url = ENDPOINTS.DELETE.replace(":id", id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete inspection record");
  }
};

export const requestInspectionUploadUrl = async (inspectionId, fileName, contentType, inspectionType) => {
  try {
    const url = ENDPOINTS.UPLOAD_REQUEST.replace(":id", inspectionId);
    const response = await axios.post(url, { fileName, contentType, inspectionType });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to obtain presigned upload URL for inspection");
  }
};

/**
 * Attach an uploaded file to one item. `kind` says which list it belongs to:
 * 'photo' (proof photos and videos, the default) or 'invoice' (PDF or image).
 */
export const confirmInspectionUpload = async (inspectionId, key, inspectionIndex, kind = 'photo') => {
  try {
    const url = ENDPOINTS.CONFIRM_UPLOAD.replace(":id", inspectionId);
    const response = await axios.post(url, { key, inspectionIndex, kind });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to confirm attachment upload for inspection");
  }
};

/**
 * Record, reopen or annotate the completion of one item. Open to managers and
 * technical staff for their own properties, not just the company login.
 *   { completedDate: 'YYYY-MM-DD', performedBy: 'in_house'|'contractor', notes }
 *                                    -> record a completion (rolls the due date).
 *                                       Needs a photo already attached, plus an
 *                                       invoice when a contractor did the work;
 *                                       otherwise 400 with `problems: [string]`.
 *   { completedDate: null }          -> reopen (a completion or a skip)
 *   { notes }                        -> update the completion notes
 */
export const updateItemCompletion = async (inspectionId, itemIndex, body) => {
  try {
    const url = ENDPOINTS.ITEM_COMPLETION.replace(":id", inspectionId).replace(":index", String(itemIndex));
    const response = await axios.patch(url, body, { headers: { "Content-Type": "application/json" } });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to save completion");
  }
};

/** Close this cycle without doing the inspection. A reason is required; the due date moves to the next date on the calendar. */
export const skipItemCycle = async (inspectionId, itemIndex, reason) => {
  try {
    const url = ENDPOINTS.SKIP_ITEM.replace(":id", inspectionId).replace(":index", String(itemIndex));
    const response = await axios.post(url, { reason }, { headers: { "Content-Type": "application/json" } });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to skip this cycle");
  }
};

/** One row per inspection line due in `year`: { propertyId, buildingName?, year, frequencyId?, overdueOnly? }. */
export const getInspectionSchedule = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.SCHEDULE, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to load the inspection schedule");
  }
};

export const getInspectionYears = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.YEARS, { params });
    return response.data.data || [];
  } catch (error) {
    console.error("Failed to load years", error);
    return [];
  }
};

/** The line's last cycle is closed and the next one is being recorded: archive it and clear the block. */
export const openItemCycle = async (inspectionId, itemIndex) => {
  try {
    const url = ENDPOINTS.OPEN_CYCLE.replace(":id", inspectionId).replace(":index", String(itemIndex));
    const response = await axios.post(url, {}, { headers: { "Content-Type": "application/json" } });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to start the next cycle");
  }
};

export const removeInspectionLine = async (inspectionId, itemIndex) => {
  try {
    const url = ENDPOINTS.REMOVE_ITEM.replace(":id", inspectionId).replace(":index", String(itemIndex));
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to remove the inspection");
  }
};

export const removeInspectionAttachment = async (inspectionId, itemIndex, key) => {
  try {
    const url = ENDPOINTS.REMOVE_ATTACHMENT.replace(":id", inspectionId).replace(":index", String(itemIndex));
    const response = await axios.delete(url, { data: { key } });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to remove attachment");
  }
};

export const getInspectionHistory = async (inspectionId, params = {}) => {
  try {
    const url = ENDPOINTS.HISTORY.replace(":id", inspectionId);
    const response = await axios.get(url, { params });
    return response.data.data || [];
  } catch (error) {
    handleApiError(error, "Failed to load history");
    return [];
  }
};

export const getInspectionAttachmentSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const response = await axios.post(ENDPOINTS.SIGNED_URLS, { keys });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to generate signed URLs for inspection attachments");
    return {};
  }
};

export const exportInspectionsToExcel = async (params = {}) => {
  try {
    toast.info("Generating Excel file...");
    const response = await axios.get(ENDPOINTS.EXPORT_EXCEL, { params, responseType: 'blob' });
    const contentDisposition = response.headers['content-disposition'];
    let fileName = `Inspection_Tracker_${new Date().toISOString().split('T')[0]}.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match?.[1]) fileName = match[1];
    }
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Excel file downloaded successfully");
  } catch (error) {
    handleApiError(error, "Failed to export inspections to Excel");
  }
};

export const getInspectionTypes = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.TYPES_BASE, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inspection types");
  }
};

export const createInspectionType = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.TYPES_BASE, payload, { headers: { "Content-Type": "application/json" } });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create inspection type");
  }
};

export const updateInspectionType = async (id, payload) => {
  try {
    const url = ENDPOINTS.TYPE_BY_ID.replace(":id", id);
    const response = await axios.patch(url, payload, { headers: { "Content-Type": "application/json" } });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update inspection type");
  }
};

export const deleteInspectionType = async (id) => {
  try {
    const url = ENDPOINTS.TYPE_BY_ID.replace(":id", id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete inspection type");
  }
};

export const getInspectionFrequencies = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.FREQUENCIES_BASE, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inspection frequencies");
  }
};

export const createInspectionFrequency = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.FREQUENCIES_BASE, payload, { headers: { "Content-Type": "application/json" } });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create inspection frequency");
  }
};

export const updateInspectionFrequency = async (id, payload) => {
  try {
    const url = ENDPOINTS.FREQUENCY_BY_ID.replace(":id", id);
    const response = await axios.patch(url, payload, { headers: { "Content-Type": "application/json" } });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update inspection frequency");
  }
};

export const deleteInspectionFrequency = async (id) => {
  try {
    const url = ENDPOINTS.FREQUENCY_BY_ID.replace(":id", id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete inspection frequency");
  }
};